import {Expressions} from '@packages/systems/wfdl';
import type {Expression} from '@packages/systems/wfdl-ast';
import {weakMemo} from '@packages/utilities/memo';
import * as ComponentLocalizations from './ComponentLocalizations';
import {getLocalizationInfoFromExpression} from './localizationEditorUtils';
import type {ComponentLocalization, LocalizationView} from './types';

/** Resolves a binding to the localization it points at, if any. */
export type LocalizationBindingResolver = (binding: {
  componentId: string;
  key: string;
}) => LocalizationView | undefined;

/**
 * The one definition of "where are this expression's localization bindings".
 * Matching goes through `getLocalizationInfoFromExpression`, which deconstructs
 * a call first, so a scoped binding matches at its whole `Call` node rather than
 * at the `Select` inside it.
 */
export const findBindingPaths = (
  expr: Expression,
  {descendIntoElements = false}: {descendIntoElements?: boolean} = {}
) =>
  Expressions.findPathsWithStoppable(expr, (node) => {
    if (!descendIntoElements && Expressions.isElement(node)) {
      return Expressions.stop(false);
    }
    if (getLocalizationInfoFromExpression(node)) return Expressions.stop(true);
    return Expressions.resume(false);
  });

/**
 * The localization bindings an expression carries, for callers that need to
 * inspect them rather than replace them. Stops at nested elements, like
 * `substituteLocalizationBindings`.
 */
export const findLocalizationBindings = weakMemo((expr: Expression) =>
  findBindingPaths(expr).flatMap((path) => {
    const node = Expressions.getIn(expr, path);
    const binding = node ? getLocalizationInfoFromExpression(node) : null;
    return binding ? [binding] : [];
  })
);

/**
 * Replaces the localization bindings in an expression with their content.
 * Stops at nested elements, so callers apply it one element at a time.
 *
 * A binding with no value for the locale resolves to the definition default, so
 * a changed expression does not by itself mean the locale translated anything --
 * see `findLocalizationBindings` for callers that need to tell those apart.
 */
export const substituteLocalizationBindings = weakMemo(
  (resolveBinding: LocalizationBindingResolver) =>
    weakMemo((expr: Expression): Expression => {
      const paths = findBindingPaths(expr);

      return paths.reduce((currentExpr, path) => {
        const node = Expressions.getIn(currentExpr, path);
        if (!node) return currentExpr;

        const locInfo = getLocalizationInfoFromExpression(node);
        if (!locInfo) return currentExpr;

        const localization = resolveBinding(locInfo);
        if (!localization) return currentExpr;

        return Expressions.setIn(
          currentExpr,
          path,
          ComponentLocalizations.getValueWithFallback(localization)
        );
      }, expr);
    })
);

/**
 * `substituteLocalizationBindings` resolved against a single component's
 * localizations, whichever component the binding names.
 */
export const substituteLocalizationBindingsInExpr = weakMemo(
  (componentLocalization: ComponentLocalization) =>
    substituteLocalizationBindings((binding) =>
      ComponentLocalizations.getLocalization(componentLocalization, binding.key)
    )
);
